import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PaymentReceiptViewStore } from "../../store";
import { PaymentReceiptView } from "../view/PaymentReceiptView";
import { PaymentReceiptUpdateStore } from "../../store";
import { PaymentReceiptUpdate } from "../update/PaymentReceiptUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PaymentReceiptModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const PaymentReceiptModalContent: React.FC<PaymentReceiptModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new PaymentReceiptUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new PaymentReceiptViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <PaymentReceiptView
            paymentReceiptViewStore={storeInfo.store as PaymentReceiptViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <PaymentReceiptUpdate
            paymentReceiptUpdateStore={
              storeInfo.store as PaymentReceiptUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
